/**
 * GLB import: parse an ArrayBuffer with `GLTFLoader`, classify the model as rigged or unrigged,
 * normalize it into one identity `Group` root and report everything the editor needs to route the
 * user into the MAP or the RIG workflow.
 *
 * Two landmines shape this module (see digests/r-gltf-export.md):
 *  - `GLTFLoader` sanitizes and uniquifies node names on reload, and `PropertyBinding` refuses
 *    names containing `[ ] . : /`. Animation track names are `"<boneName>.quaternion"`, so bone
 *    names must be sanitize-safe AND unique *before* anything is mapped or exported. We enforce
 *    that here, once, at import time (plan D17).
 *  - Unrigged models arrive with arbitrary node transforms. Placement, skinning and export all
 *    assume one shared model space, so mesh world matrices are baked into geometry (plan D20).
 *
 * Never fails silently: every unsupported input throws an `AppError`.
 */

import { Box3, Bone, Group, Matrix4, Mesh, PropertyBinding, SkinnedMesh } from 'three';
import type { AnimationClip, BufferGeometry, Material, Object3D, Skeleton } from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import type { GLTF } from 'three/addons/loaders/GLTFLoader.js';

import { AppError } from '../shared';
import { createLocalOnlyManager } from './localOnly';

/** A skeleton with fewer bones than this is not a usable humanoid rig. */
export const MIN_RIGGED_BONE_COUNT = 6;

export interface ImportedModel {
  /** One `Group` at identity holding the whole model. */
  root: Group;
  kind: 'rigged' | 'unrigged';
  /** Plain (non-skinned) meshes. For unrigged models their geometry is already world-baked. */
  meshes: Mesh[];
  skinnedMeshes: SkinnedMesh[];
  /** Skeleton of the first usable skinned mesh, or `null` for unrigged models. */
  skeleton: Skeleton | null;
  /** Every bone name in the scene, hierarchy order, after sanitizing/uniquifying. */
  sourceBoneNames: string[];
  embeddedClips: AnimationClip[];
  warnings: string[];
  /** Rest-pose world bounds of all mesh geometry. */
  bounds: Box3;
  /** Y extent of `bounds`. */
  height: number;
  /**
   * `originalName -> finalName` for bones renamed on import. When several bones shared one
   * original name only the last rename is recorded here (the map is diagnostic); every rename is
   * also listed in `warnings`.
   */
  renamedBones: Record<string, string>;
  /**
   * Live material → its index in the source file's `materials` array.
   *
   * The join between a material on screen and the block of glTF JSON that says which textures it
   * had, which is what makes a texture-free export re-attachable: the manifest is keyed by this
   * index and the stripped GLB carries it in each material's `extras`. Built from
   * `GLTFParser.associations`, so it costs nothing and is exact even when several materials share
   * a name (glTF does not require unique material names — see `glb-export-bone-names.md` for what
   * this project already learned about trusting names).
   *
   * Empty for a model whose loader gave no associations. Everything downstream degrades to
   * matching by name, so an empty map is a smaller guarantee, never a failure.
   */
  materialSource: Map<Material, number>;
}

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

function parseGltf(buffer: ArrayBuffer): Promise<GLTF> {
  return new Promise<GLTF>((resolve, reject) => {
    if (!(buffer instanceof ArrayBuffer) || buffer.byteLength === 0) {
      reject(new AppError('GLB_PARSE_FAILED', 'The file is empty or is not a binary buffer.'));
      return;
    }
    // Local-only manager: an imported GLB must never be able to make the app fetch anything.
    const loader = new GLTFLoader(createLocalOnlyManager('model'));
    try {
      loader.parse(
        buffer,
        '',
        (gltf) => resolve(gltf),
        (event: unknown) => {
          reject(
            new AppError(
              'GLB_PARSE_FAILED',
              `The GLB could not be read: ${describeError(event)}`,
              event,
            ),
          );
        },
      );
    } catch (error) {
      reject(
        new AppError('GLB_PARSE_FAILED', `The GLB could not be read: ${describeError(error)}`, error),
      );
    }
  });
}

function describeError(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return String(error);
}

// ---------------------------------------------------------------------------
// Bone names
// ---------------------------------------------------------------------------

function collectBones(root: Object3D): Bone[] {
  const bones: Bone[] = [];
  root.traverse((o) => {
    if ((o as Bone).isBone === true) bones.push(o as Bone);
  });
  return bones;
}

/**
 * Rewrite every bone name under `root` so it survives `PropertyBinding.parseTrackName` and is
 * unique within the file (including against non-bone node names, which `GLTFLoader` uniquifies
 * from the same pool). Duplicates get `_1`, `_2`, ... suffixes.
 *
 * Returns `originalName -> finalName` for the bones that actually changed. Exported because RIG
 * mode and tests need the same guarantee for skeletons that were built rather than imported.
 */
export function enforceUniqueSafeBoneNames(root: Object3D): Record<string, string> {
  const renamed: Record<string, string> = {};
  const bones = collectBones(root);
  const boneSet = new Set<Object3D>(bones);

  // Seed with the non-bone node names so a bone can never collide with a mesh on reload.
  const used = new Set<string>();
  root.traverse((o) => {
    if (!boneSet.has(o) && o.name) used.add(o.name);
  });

  for (const bone of bones) {
    const original = bone.name;
    let candidate = PropertyBinding.sanitizeNodeName(original ?? '');
    if (candidate === '') candidate = 'Bone';
    if (used.has(candidate)) {
      let suffix = 1;
      while (used.has(`${candidate}_${suffix}`)) suffix++;
      candidate = `${candidate}_${suffix}`;
    }
    used.add(candidate);
    if (candidate !== original) {
      renamed[original] = candidate;
      bone.name = candidate;
    }
  }

  return renamed;
}

/** True when a bone name can appear in a `"<name>.quaternion"` animation track. */
export function isTrackSafeName(name: string): boolean {
  return name.length > 0 && PropertyBinding.sanitizeNodeName(name) === name;
}

// ---------------------------------------------------------------------------
// Root normalization
// ---------------------------------------------------------------------------

const IDENTITY = new Matrix4();

function isIdentityTransform(o: Object3D): boolean {
  return (
    o.position.x === 0 &&
    o.position.y === 0 &&
    o.position.z === 0 &&
    o.quaternion.x === 0 &&
    o.quaternion.y === 0 &&
    o.quaternion.z === 0 &&
    o.quaternion.w === 1 &&
    o.scale.x === 1 &&
    o.scale.y === 1 &&
    o.scale.z === 1
  );
}

/** Move every child of `scene` under a fresh `Group` sitting at identity. */
function reparentUnderIdentityRoot(scene: Group, warnings: string[]): Group {
  const root = new Group();
  root.name = scene.name || 'ImportedRoot';
  Object.assign(root.userData, scene.userData);

  const children = [...scene.children];
  const sceneIsIdentity = isIdentityTransform(scene);
  if (!sceneIsIdentity) scene.updateMatrix();
  const sceneMatrix = sceneIsIdentity ? IDENTITY : scene.matrix.clone();

  for (const child of children) {
    scene.remove(child);
    root.add(child);
    if (!sceneIsIdentity) child.applyMatrix4(sceneMatrix);
  }

  if (!sceneIsIdentity) {
    warnings.push(
      'The glTF scene node had a non-identity transform; it was folded into its children so the ' +
        'model root sits at identity.',
    );
  }

  root.updateMatrixWorld(true);
  return root;
}

// ---------------------------------------------------------------------------
// Materials
// ---------------------------------------------------------------------------

/**
 * Live material → source glTF material index, for every material reachable from `scene`.
 *
 * `GLTFParser.associations` is three's own record of what it built from what, and it survives the
 * loader's material caching: `assignFinalMaterial` clones a material when a mesh needs vertex
 * colours or flat shading and copies the association onto the clone
 * (`GLTFLoader.js:3517`), so a cloned material still resolves to the right source index.
 *
 * The scene is traversed rather than the map iterated because `associations` also holds materials
 * from scenes that were not loaded, and only what is actually on screen can be exported.
 */
function collectMaterialSources(gltf: GLTF, scene: Object3D): Map<Material, number> {
  const sources = new Map<Material, number>();
  const associations = gltf.parser?.associations;
  if (!associations) return sources;

  scene.traverse((o) => {
    const mesh = o as Mesh;
    if (mesh.isMesh !== true || !mesh.material) return;
    for (const material of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) {
      if (!material || sources.has(material)) continue;
      const reference = associations.get(material) as { materials?: number } | undefined;
      if (typeof reference?.materials === 'number') sources.set(material, reference.materials);
    }
  });

  return sources;
}

// ---------------------------------------------------------------------------
// Bounds
// ---------------------------------------------------------------------------

function geometryWorldBounds(meshes: readonly Mesh[]): Box3 {
  const box = new Box3();
  const scratch = new Box3();
  for (const mesh of meshes) {
    const geometry: BufferGeometry = mesh.geometry;
    if (!geometry.boundingBox) geometry.computeBoundingBox();
    if (!geometry.boundingBox) continue;
    scratch.copy(geometry.boundingBox).applyMatrix4(mesh.matrixWorld);
    box.union(scratch);
  }
  return box;
}

// ---------------------------------------------------------------------------
// Unrigged normalization (plan D20)
// ---------------------------------------------------------------------------

function bakeWorldTransformsIntoGeometry(scene: Group, warnings: string[]): {
  root: Group;
  meshes: Mesh[];
} {
  scene.updateMatrixWorld(true);

  const sources: Mesh[] = [];
  scene.traverse((o) => {
    if ((o as Mesh).isMesh === true) sources.push(o as Mesh);
  });

  // Snapshot world matrices before any reparenting touches the graph.
  const worldMatrices = sources.map((m) => m.matrixWorld.clone());

  const root = new Group();
  root.name = scene.name || 'ImportedRoot';
  Object.assign(root.userData, scene.userData);

  const baked: Mesh[] = [];

  for (let i = 0; i < sources.length; i++) {
    const source = sources[i];
    const geometry = source.geometry.clone();
    geometry.applyMatrix4(worldMatrices[i]); // transforms position, normal and tangent
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();

    let mesh: Mesh;
    if ((source as SkinnedMesh).isSkinnedMesh === true) {
      geometry.deleteAttribute('skinIndex');
      geometry.deleteAttribute('skinWeight');
      mesh = new Mesh(geometry, source.material as Material | Material[]);
      mesh.name = source.name;
      warnings.push(
        `Mesh "${source.name || '(unnamed)'}" was skinned but its skeleton is unusable; it was ` +
          'flattened to a plain mesh for the unrigged workflow.',
      );
    } else {
      source.removeFromParent();
      source.geometry = geometry;
      mesh = source;
    }

    mesh.position.set(0, 0, 0);
    mesh.quaternion.identity();
    mesh.scale.set(1, 1, 1);
    mesh.updateMatrix();
    root.add(mesh);
    baked.push(mesh);
  }

  root.updateMatrixWorld(true);
  return { root, meshes: baked };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Import a GLB that is already in memory. `GLTFLoader.parse` is used directly (no fetch), so this
 * works identically in the editor, in tests and in the runtime demo.
 *
 * @throws AppError `GLB_PARSE_FAILED`, `GLB_UNSUPPORTED` or `GLB_NO_MESH`.
 */
export async function importGlbFromArrayBuffer(buffer: ArrayBuffer): Promise<ImportedModel> {
  const gltf = await parseGltf(buffer);
  const warnings: string[] = [];

  const scene = gltf.scene ?? gltf.scenes?.[0];
  if (!scene) {
    throw new AppError(
      'GLB_UNSUPPORTED',
      'The GLB contains no scene, so there is nothing to import.',
    );
  }
  if (gltf.scenes && gltf.scenes.length > 1) {
    warnings.push(
      `The GLB contains ${gltf.scenes.length} scenes; only the default scene was imported.`,
    );
  }

  scene.updateMatrixWorld(true);

  const skinnedMeshes: SkinnedMesh[] = [];
  const plainMeshes: Mesh[] = [];
  scene.traverse((o) => {
    if ((o as SkinnedMesh).isSkinnedMesh === true) skinnedMeshes.push(o as SkinnedMesh);
    else if ((o as Mesh).isMesh === true) plainMeshes.push(o as Mesh);
  });

  if (skinnedMeshes.length === 0 && plainMeshes.length === 0) {
    throw new AppError(
      'GLB_NO_MESH',
      'The GLB contains no mesh. Import a model with geometry, not an animation-only or ' +
        'camera-only file.',
    );
  }

  const rigged = skinnedMeshes.find(
    (m) => m.skeleton != null && m.skeleton.bones.length >= MIN_RIGGED_BONE_COUNT,
  );

  if (!rigged && skinnedMeshes.length > 0) {
    const best = skinnedMeshes.reduce(
      (n, m) => Math.max(n, m.skeleton?.bones.length ?? 0),
      0,
    );
    warnings.push(
      `The model is skinned but its largest skeleton has only ${best} bone(s) ` +
        `(at least ${MIN_RIGGED_BONE_COUNT} are needed); it is treated as unrigged.`,
    );
  }

  const embeddedClips = gltf.animations ?? [];

  // Collected off the ORIGINAL scene, before either branch reparents or rebuilds anything: both
  // paths carry the same material objects through by reference (`bakeWorldTransformsIntoGeometry`
  // below, `skeletonBuilder.createSkinnedMesh` later), so the map stays valid either way.
  const materialSource = collectMaterialSources(gltf, scene);

  if (rigged) {
    const root = reparentUnderIdentityRoot(scene, warnings);
    const renamedBones = enforceUniqueSafeBoneNames(root);
    for (const [from, to] of Object.entries(renamedBones)) {
      warnings.push(`Bone "${from}" was renamed to "${to}" to keep bone names unique and safe.`);
    }

    root.updateMatrixWorld(true);

    const bounds = geometryWorldBounds(skinnedMeshes.length > 0 ? skinnedMeshes : plainMeshes);

    return {
      root,
      kind: 'rigged',
      meshes: plainMeshes,
      skinnedMeshes,
      skeleton: rigged.skeleton,
      sourceBoneNames: collectBones(root).map((b) => b.name),
      embeddedClips,
      warnings,
      bounds,
      height: bounds.isEmpty() ? 0 : bounds.max.y - bounds.min.y,
      renamedBones,
      materialSource,
    };
  }

  const { root, meshes } = bakeWorldTransformsIntoGeometry(scene, warnings);
  const bounds = geometryWorldBounds(meshes);

  return {
    root,
    kind: 'unrigged',
    meshes,
    skinnedMeshes: [],
    skeleton: null,
    sourceBoneNames: [],
    embeddedClips,
    warnings,
    bounds,
    height: bounds.isEmpty() ? 0 : bounds.max.y - bounds.min.y,
    renamedBones: {},
    materialSource,
  };
}

/** Import from a picked `File` (or any `Blob`). */
export async function importGlbFromFile(file: File | Blob): Promise<ImportedModel> {
  let buffer: ArrayBuffer;
  try {
    buffer = await file.arrayBuffer();
  } catch (error) {
    throw new AppError('GLB_PARSE_FAILED', `The file could not be read: ${describeError(error)}`, error);
  }
  return importGlbFromArrayBuffer(buffer);
}

/**
 * Import from a URL. Only for the dev demo pages — the editor never fetches at runtime
 * (BRIEF-COMMON: "No network calls at runtime").
 */
export async function importGlbFromUrl(url: string): Promise<ImportedModel> {
  let buffer: ArrayBuffer;
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status} ${response.statusText}`);
    }
    buffer = await response.arrayBuffer();
  } catch (error) {
    throw new AppError(
      'GLB_PARSE_FAILED',
      `The GLB at "${url}" could not be downloaded: ${describeError(error)}`,
      error,
    );
  }
  return importGlbFromArrayBuffer(buffer);
}
